import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import ApiAdmin from "../../config/AppAdmin";
import dynamicSocketUrl from "../../config/socketUrl";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const fetchOrders = async () => {
  const response = await ApiAdmin.get(`/joinOrder`);
  if (response.status !== 200) {
    throw new Error("Failed to fetch orders");
  }
  return response.data.data || [];
};

const acknowledgementKey = () => {
  try {
    const admin = JSON.parse(localStorage.getItem("adminData") || "{}");
    return `onlineOrderAcknowledgements:${
      (admin.data || admin).aid || "current"
    }`;
  } catch {
    return "onlineOrderAcknowledgements:current";
  }
};
const acknowledgedOrders = () => {
  try {
    return JSON.parse(localStorage.getItem(acknowledgementKey()) || "{}");
  } catch {
    return {};
  }
};
export const isNewOrder = (order) => {
  const id = order.order_id ?? order.id ?? order.tracking_id;
  if (id != null && acknowledgedOrders()[id]) return false;
  if (order.is_seen != null)
    return (
      order.is_seen === 0 || order.is_seen === "0" || order.is_seen === false
    );
  return (
    String(order.track_status || order.status || "")
      .replace(/"/g, "")
      .trim()
      .toLowerCase() === "pending"
  );
};
let orderStream = null;
const subscribers = new Set();
const useOrders = () => {
  const client = useQueryClient();
  const { data, refetch, isLoading, isError } = useQuery({
    queryKey: ["orders"],
    queryFn: fetchOrders,
    refetchInterval: 30000,
  });
  useEffect(() => {
    const refresh = () => client.invalidateQueries({ queryKey: ["orders"] });
    subscribers.add(refresh);
    if (!orderStream) {
      orderStream = new EventSource(`${dynamicSocketUrl()}/bbgapi/sse`, {
        withCredentials: true,
      });
      orderStream.addEventListener("newOrder", (event) => {
        let message = "New order received";
        try {
          message = JSON.parse(event.data)?.message || message;
        } catch {}
        subscribers.forEach((notify) => notify());
        toast.info(message, {
          position: "top-right",
          toastId: event.lastEventId || "new-online-order",
        });
      });
    }
    return () => {
      subscribers.delete(refresh);
      if (!subscribers.size) {
        orderStream?.close();
        orderStream = null;
      }
    };
  }, [client]);
  const newOrderCount = (Array.isArray(data) ? data : []).filter(
    isNewOrder
  ).length;
  return {
    data,
    refetch,
    isLoading,
    isError,
    newOrderCount,
    resetOrderCount: refetch,
  };
};

export const markSeenOrders = async (orderId) => {
  const response = await ApiAdmin.patch(`/joinOrder/mark-seen/${orderId}`);
  if (response.data?.success === false)
    throw new Error("Unable to mark order as seen");
  try {
    localStorage.setItem(
      acknowledgementKey(),
      JSON.stringify({ ...acknowledgedOrders(), [orderId]: true })
    );
  } catch {}
};
export default useOrders;
